import React, { FunctionComponent, useEffect, useState } from "react";
import StatusIndicator from "aws-northstar/components/StatusIndicator";
import Table, { Column } from "aws-northstar/components/Table";
import Flashbar, { FlashbarMessage } from "aws-northstar/components/Flashbar";
import { useSelector } from "react-redux";

import { IRequest, ReduxRoot } from "../../interfaces";
import { getProcessedRequests } from "../../data";
import { translateAccountId } from "../../config";

const columnDefinitions: Column<IRequest>[] = [
    {
        id: "requester",
        width: 200,
        Header: "Requester",
        accessor: "requester",
    },
    {
        id: "reviewer",
        width: 200,
        Header: "Reviewer",
        accessor: "reviewer",
    },
    {
        id: "request_account",
        width: 200,
        Header: "Account",
        Cell: ({ row }: any) => {
            if (row && row.original && row.original.request_account) {
                return translateAccountId(row.original.request_account);
            }

            return row.original?.request_account || "";
        },
    },
    {
        id: "request_role",
        width: 200,
        Header: "Role",
        accessor: "request_role",
    },
    {
        id: "request_duration",
        width: 100,
        Header: "Duration",
        accessor: "request_duration",
    },
    {
        id: "request_justification",
        width: 200,
        Header: "Justification",
        accessor: "request_justification",
    },
    {
        id: "request_status",
        width: 100,
        Header: "Status",
        Cell: ({ row }: any) => {
            if (row && row.original) {
                const status = row.original.request_status;

                switch (status) {
                    case "Requested":
                        return <StatusIndicator statusType="info">Requested</StatusIndicator>;
                    case "Approved":
                        return <StatusIndicator statusType="positive">Approved</StatusIndicator>;
                    case "Rejected":
                        return <StatusIndicator statusType="negative">Rejected</StatusIndicator>;
                    case "Expired":
                        return <StatusIndicator statusType="negative">Expired</StatusIndicator>;
                    case "Ended":
                        return <StatusIndicator statusType="negative">Ended</StatusIndicator>;
                    default:
                        return null;
                }
            }

            return row.id;
        },
    },
    {
        id: "request_time",
        width: 150,
        Header: "Requested Time",
        accessor: "request_time",
    },
    {
        id: "expiration_time",
        width: 150,
        Header: "Expiration Time",
        accessor: "expiration_time",
    },
];

const ReviewedTable: FunctionComponent = () => {
    const userInfo = useSelector((state: ReduxRoot) => {
        return state.breakGlassReducerState.userInfo;
    });

    const [requests, setRequests] = useState<IRequest[]>([]);
    const [selectedItems, setSelectedItems] = useState<object[]>([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = React.useState<FlashbarMessage[]>([]);

    useEffect(() => {
        const getAllRequests = async () => {
            try {
                setLoading(true);

                const requests = await getProcessedRequests().then((result: IRequest[]) => {
                    return result;
                });

                // Sort by request_time (latest first)
                const sortedRequests = requests.sort((a, b) => {
                    const dateA = new Date(a.request_time || 0);
                    const dateB = new Date(b.request_time || 0);

                    return dateB.getTime() - dateA.getTime();
                });

                setRequests(sortedRequests);

                await Promise.resolve();
                // }
                setLoading(false);
            } catch (err) {
                console.error(err.toString());
                const items: FlashbarMessage[] = [
                    {
                        header: "Could not get the reviewed requests: " + err.toString(),
                        type: "error",
                        dismissible: true,
                    },
                ];

                setErrors(items);
            }
        };

        getAllRequests().then(() => console.log("getProcessedRequests() completed."));
    }, [userInfo]);

    const handleSelectionChange = (items: object[]) => {
        if (!(selectedItems.length === 0 && items.length === 0)) {
            setSelectedItems(items);
        }
    };

    return (
        <div>
            <Table
                columnDefinitions={columnDefinitions}
                items={requests}
                tableTitle={"Requests already reviewed"}
                onSelectionChange={handleSelectionChange}
                loading={loading}
                // actionGroup={tableActions}
                multiSelect={false}
            />
            <Flashbar items={errors} />
        </div>
    );
};

export default ReviewedTable;
